import { createBrowserRouter, RouterProvider } from "react-router-dom";

// MANTINE IMPORTS
import "@mantine/carousel/styles.css";
import {
  createTheme as createMantineTheme,
  MantineColorsTuple,
  MantineProvider,
} from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/dropzone/styles.css";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";

import { createContext, useEffect, useState } from "react";
import { AllFatherContextProps } from "./Context/AllFatherContext";
import { ThemeValueProps } from "./Context/AppContext";
import { LocationInformationProps, StudentProps } from "./Lib/types";

// i18n imports
import "./Lib/i18n";
import { LanguageProvider } from "./Lib/i18n/LanguageContext";

import ScrollToTop from "./Components/ScrollToTop";
import Courses from "./Pages/Courses";
import Dashboard from "./Pages/Dashboard";
import Login from "./Pages/Login";
import Overview from "./Pages/Overview";
import PlansNoAuth from "./Pages/PlansNoAuth";
import Signup from "./Pages/Signup";
import SingleCourse from "./Pages/SingleCourse";

import MegaLoader from "./Components/MegaLoader";
import { HttpSuccessCodesObj } from "./Lib/data";
import { Endpoints } from "./Lib/endpoints";
import { GetAssets, getIpAddress } from "./Lib/methods";
import { AssetsResponse, NonPaginatedResponse } from "./Lib/responses";
import { PerformRequest, usePerformRequest } from "./Lib/usePerformRequest";
import About from "./Pages/About";
import Account from "./Pages/Account";
import CompleteResetPassword from "./Pages/Auth/CompleteResetPassword";
import RequestResetPassword from "./Pages/Auth/RequestResetPassword";
import Blog from "./Pages/Blogs";
import Careers from "./Pages/Careers";
import Certificate from "./Pages/Certificate";
import CourseAssessments from "./Pages/CourseAssessments";
import CoursePerformance from "./Pages/CoursePerformance";
import CourseRecordings from "./Pages/CourseRecordings";
import Error404 from "./Pages/Error404";
import Events from "./Pages/Events";
import Home from "./Pages/Home";
import LessonMaterials from "./Pages/LessonMaterials";
import PrivacyPolicy from "./Pages/PrivacyPolicy";
import PurchasePlan from "./Pages/PurchasePlan";
import SearchPage from "./Pages/Search";
import ServerDownView from "./Pages/ServerDownView";
import SingleAssessment from "./Pages/SingleAssessment";
import SingleBlog from "./Pages/SingleBlog";
import SingleLessonLiveKit from "./Pages/SingleLessonLiveKit";
import SingleRecording from "./Pages/SingleRecording";
import InitPage from "./Pages/Init";
import TermsAndConditions from "./Pages/TermsAndConditions";
import Test from "./Pages/Test";
import VerifyEmail from "./Pages/VerifyEmail";
import "./styles/index.scss";
import ValidatePaystackPayment from "./Pages/ValidatePaystackPayment";
// Enhanced AllFatherContext with theme support
export type EnhancedAllFatherContextProps = AllFatherContextProps & {
  theme: ThemeValueProps;
  setTheme: (theme: ThemeValueProps) => void;
  toggleTheme: () => void;
};

// Custom colors for better dark mode support
const customColors: MantineColorsTuple = [
  "#f0f0f0",
  "#e0e0e0",
  "#c1c2c5",
  "#a6a7ab",
  "#909296",
  "#5c5f66",
  "#373a40",
  "#2c2e33",
  "#25262b",
  "#1a1b1e",
];

const mantineTheme = createMantineTheme({
  colors: {
    dark: customColors,
  },
  primaryColor: "violet",
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  headings: {
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  },
});

const AllFatherContext = createContext<EnhancedAllFatherContextProps | null>(null);

export { AllFatherContext };
export default function App() {
  const [isLocationInformationLoading, setLocationInformationLoading] = useState(true);
  const [ipAddress, setIpAddress] = useState<string | null>(null);
  const [locationInformation, setLocationInformation] = useState<LocationInformationProps | null>(
    null
  );

  useEffect(() => {
    getIpAddress()
      .then((ipAddress) => {
        setIpAddress(ipAddress);
        // setLocationInformationLoading(false);
      })
      .catch((error) => {
        setLocationInformationLoading(false);
      });
  }, []);

  const { data: isDiscountOngoing, isLoading: isEarlyBirdDiscountLoading } = usePerformRequest<
    NonPaginatedResponse<{
      isDiscountOngoing: {
        isDiscountOngoing: boolean;
        discount: number;
      };
    }>
  >({
    method: Endpoints.GetStudentEarlyBirdDiscount().method,
    route: Endpoints.GetStudentEarlyBirdDiscount().route,
  });

  const GetLocationInformationUsingIpAddress = async () => {
    setLocationInformationLoading(true);
    const r = await PerformRequest<NonPaginatedResponse<LocationInformationProps>>({
      method: Endpoints.GetStudentLocation().method,
      route: Endpoints.GetStudentLocation().route,
      data: {
        ipAddress,
      },
      callback: () => {
        setLocationInformationLoading(false);
      },
    });
    setLocationInformationLoading(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        setLocationInformation(r?.data?.data);
        try {
          localStorage.setItem("currency", r?.data?.data?.currency);
        } catch {}
      }
    }
  };
  useEffect(() => {
    if (ipAddress) {
      GetLocationInformationUsingIpAddress();
    }
  }, [ipAddress]);

  const router = createBrowserRouter([
    {
      path: "/",
      element: <Home />,
    },
    {
      path: "/init",
      element: <InitPage />,
    },
    {
      path: "/test",
      element: <Test />,
    },
    {
      path: "/about",
      element: <About />,
    },
    {
      path: "/search",
      element: <SearchPage />,
    },
    {
      path: "/plans",
      element: <PlansNoAuth />,
    },
    {
      path: "/login",
      element: <Login />,
    },
    {
      path: "/forgot-password",
      element: <RequestResetPassword />,
    },
    {
      path: "/reset-password",
      element: <CompleteResetPassword />,
    },
    {
      path: "/sign-up",
      element: <Signup />,
    },
    {
      // REFERENCE IS SUPPOSEDLY transactionID
      path: "/verify/:reference",
      element: <ValidatePaystackPayment />,
    },
    {
      path: "/verify-email",
      element: <VerifyEmail />,
    },
    {
      path: "/blogs",
      element: <Blog />,
    },
    {
      path: "/blog/:blogID",
      element: <SingleBlog />,
    },
    {
      path: "/careers",
      element: <Careers />,
    },
    {
      path: "/events",
      element: <Events />,
    },
    {
      path: "/terms",
      element: <TermsAndConditions />,
    },
    {
      path: "/privacy",
      element: <PrivacyPolicy />,
    },
    {
      path: "/d",
      element: <Dashboard />,
      children: [
        {
          path: "/d/overview",
          element: <Overview />,
        },
        {
          path: "/d/",
          element: <Overview />,
        },
        {
          path: "/d/courses",
          element: <Courses />,
        },
        {
          path: "/d/course/:courseID",
          element: <SingleCourse />,
        },
        {
          path: "/d/lesson/:lessonID",
          element: <SingleLessonLiveKit />,
          // element: <SingleLesson />,
        },
        {
          path: "/d/assessments/:lessonID",
          element: <CourseAssessments />,
        },
        {
          path: "/d/performance/:courseID",
          element: <CoursePerformance />,
        },
        {
          path: "/d/certificate/:courseID",
          element: <Certificate />,
        },
        {
          path: "/d/materials/:lessonID",
          element: <LessonMaterials />,
        },

        {
          path: "/d/assessment/:assessmentID",
          element: <SingleAssessment />,
        },
        {
          path: "/d/purchase-plan/:planID",
          element: <PurchasePlan />,
        },

        {
          path: "/d/account",
          element: <Account />,
        },
        {
          path: "/d/recordings/:courseID",
          element: <CourseRecordings />,
        },
        {
          path: "/d/recording/:recordingID",
          element: <SingleRecording />,
        },
      ],
    },
    {
      path: "*",
      element: <Error404 />,
    },
  ]);

  const [theme, setTheme] = useState<ThemeValueProps>(() => {
    // Get theme from localStorage or default to light
    const savedTheme = localStorage.getItem("app-theme") as ThemeValueProps;
    return savedTheme || "light";
  });

  const { data: profile } = usePerformRequest<NonPaginatedResponse<StudentProps>>({
    method: Endpoints.GetStudentProfile().method,
    route: Endpoints.GetStudentProfile().route,
  });

  const logout = () => {
    localStorage.removeItem("t");
    window.location.href = "/login";
  };

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    setTimeout(() => {
      window.location.reload();
    }, 100); // Give time for setTheme to update localStorage
  };

  // Update localStorage and body data-theme attribute when theme changes
  useEffect(() => {
    localStorage.setItem("app-theme", theme);
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Set initial theme on mount
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, []);

  const [assets, setAssets] = useState<AssetsResponse | null>(null);
  useEffect(() => {
    GetAssets().then((a) => {
      if (a) {
        setAssets(a);
      }
    });
  }, []);

  return (
    <MantineProvider theme={mantineTheme} defaultColorScheme={theme}>
      <LanguageProvider>
        <AllFatherContext.Provider
          value={{
            profile: profile?.data || null,
            locationInformation: locationInformation ?? null,
            isLocationInformationLoading,
            isDiscountOngoing: isDiscountOngoing?.data?.isDiscountOngoing || {
              isDiscountOngoing: false,
              discount: 0,
            },

            reloadLocationInformation: GetLocationInformationUsingIpAddress,
            logout,
            theme,
            setTheme,
            toggleTheme,
            assets: assets ?? null,
          }}
        >
          <Notifications
            styles={{
              root: {
                zIndex: 9999999999999,
              },
            }}
            position="bottom-right"
          />
          {isLocationInformationLoading || isEarlyBirdDiscountLoading ? (
            <MegaLoader />
          ) : (
            <>
              {locationInformation ? (
                <>
                  <ScrollToTop />
                  <RouterProvider router={router} />
                </>
              ) : (
                <ServerDownView />
              )}
            </>
          )}
          {/* <CountryDetectionExample /> */}
        </AllFatherContext.Provider>
      </LanguageProvider>
    </MantineProvider>
  );
}
